/**
 * Canvas 2D renderer (TD §11), the prototype's look and layer order:
 *  1 background · 2 terrain · 3 screenshot · 4 plot · 5 habitat fills · 6 plaza and staff area · 7 water ·
 *  8 habitat outlines · 9 roads (timber over water) · 10 labels · 11 icons · 12 boundary · 13 grid ·
 *  14 handles and width grips · 15 tool previews · 16 scale bar.
 * Everything is drawn from the doc and its derived geometry; nothing here changes state.
 */
import { centroid, distPoly, pip, polyArea, spline, type Derived } from '../../core/geom';
import { isAnimalArea } from '../../core/model';
import type { Analysis, PlanDoc, Pt, Species } from '../../shared/types';
import type { Selection } from '../store/editor';
import { selectionHandles, widthGrips, ICON_R, ICON_R_ENTRANCE } from './hit';
import { BIOMES, ICONS, KIND_ICON, KIND_LABEL, idHash, shade, type Palette } from './palette';
import { terrainCanvas } from './terrain';
import { colName, gridStep, type Transform } from './view';

/** In-progress drawing shown on top of the map. */
export interface ToolPreview {
  /** Points placed so far (map metres). */
  points?: Pt[];
  /** Where the next point would go. */
  cursor?: Pt | null;
  /** Draw the preview as a closed outline (habitat, lake, boundary). */
  closed?: boolean;
  /** Curve the preview like a road or river. */
  curved?: boolean;
  /** Yellow ring: where a new road would join an existing one. */
  snap?: Pt | null;
  /** Orange ring: the road end picked first when joining. */
  ring?: Pt | null;
  /** A road to highlight (cut, join, delete hover). */
  hoverRoad?: string | null;
  /** An object to highlight in red (delete tool). */
  hoverDelete?: Selection;
  /** A dashed line across the road where it would be cut. */
  cutAt?: { a: Pt; b: Pt } | null;
}

/** Everything one frame needs. */
export interface Scene {
  doc: PlanDoc;
  derived: Derived;
  analysis: Analysis | null;
  species: ReadonlyMap<string, Species> | null;
  palette: Palette;
  selection: Selection;
  /** Show the selection's dots (select tool). */
  handles: boolean;
  /** Show width grips (Advanced). */
  grips: boolean;
  showGrid: boolean;
  /** The screenshot under the map, once loaded. */
  overlayImage: HTMLImageElement | null;
  preview?: ToolPreview;
  /** Pixels to lift the scale bar off the bottom edge (room for the status line). */
  scaleBarLift?: number;
  /** Show the scale bar (off for thumbnails). */
  scaleBar?: boolean;
  /** The view's zoom (1–8), for label sizes. */
  zoom?: number;
  /** Draw labels (off for tiny thumbnails). */
  labels?: boolean;
}

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');

/** Area-weighted centre of a ring, falling back to the average point when the ring is degenerate. */
function labelPoint(ring: readonly Pt[]): Pt {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const p = ring[i]!, q = ring[j]!, f = q[0] * p[1] - p[0] * q[1];
    a += f; cx += (q[0] + p[0]) * f; cy += (q[1] + p[1]) * f;
  }
  if (Math.abs(a) < 1e-6) return centroid(ring);
  const c: Pt = [cx / (3 * a), cy / (3 * a)];
  return pip(ring, c[0], c[1]) ? c : centroid(ring);
}

interface WaterShape { rings: Pt[][]; rivers: { p: Pt[]; w: number }[] }
const wetCache = new WeakMap<object, { water: WaterShape; wet: boolean[] }>();
let lastWater: WaterShape | null = null;

/** The water shapes of a plan, reused while they don't change so road "wet" flags stay cached. */
function waterOf(doc: PlanDoc, derived: Derived): WaterShape {
  const rings: Pt[][] = [];
  for (const [id, a] of Object.entries(doc.areas)) if (a.kind === 'lake' || a.kind === 'pool') { const r = derived.areaRings[id]; if (r && r.length >= 3) rings.push(r); }
  const rivers = Object.entries(doc.rivers).flatMap(([id, rv]) => { const p = derived.riverPolys[id]; return p && p.length >= 2 ? [{ p, w: rv.w }] : []; });
  const lw = lastWater;
  if (lw && lw.rings.length === rings.length && lw.rings.every((r, i) => r === rings[i]) && lw.rivers.length === rivers.length
    && lw.rivers.every((r, i) => r.p === rivers[i]!.p && r.w === rivers[i]!.w)) return lw;
  lastWater = { rings, rivers };
  return lastWater;
}

/** Which segments of a road lie over water (drawn as bridges or boardwalks in timber). */
function wetSegments(poly: { pts: Pt[] }, water: WaterShape): boolean[] {
  const hit = wetCache.get(poly);
  if (hit && hit.water === water) return hit.wet;
  const P = poly.pts, wet: boolean[] = [];
  for (let i = 0; i < P.length - 1; i++) {
    const x = (P[i]![0] + P[i + 1]![0]) / 2, y = (P[i]![1] + P[i + 1]![1]) / 2;
    wet.push(water.rings.some((r) => pip(r, x, y)) || water.rivers.some((r) => distPoly(r.p, x, y, false) <= r.w / 2));
  }
  wetCache.set(poly, { water, wet });
  return wet;
}

/** Draws one frame. `g` must be sized to t.w × t.h CSS pixels times `dpr`. */
export function drawScene(g: CanvasRenderingContext2D, t: Transform, dpr: number, s: Scene): void {
  const { doc, derived, palette: pal } = s, k = t.k, u = 1 / k, M = doc.size;
  const sel = s.selection;
  const zoom = s.zoom ?? 1, k1 = k / zoom;
  const isSel = (kind: string, id: string) => !!sel && sel.kind === kind && 'id' in sel && sel.id === id;
  const trace = (poly: readonly Pt[], close: boolean) => {
    g.beginPath();
    poly.forEach((p, i) => (i ? g.lineTo(p[0], p[1]) : g.moveTo(p[0], p[1])));
    if (close) g.closePath();
  };
  const overlayOn = !!(doc.overlay && s.overlayImage);

  // 1 background
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.fillStyle = pal.dark ? '#1d2219' : '#d9d3c0';
  g.fillRect(0, 0, t.w, t.h);
  g.setTransform(dpr * k, 0, 0, dpr * k, dpr * t.ox, dpr * t.oy);
  g.fillStyle = pal.ground;
  g.fillRect(0, 0, M, M);

  // 2 terrain
  const terr = terrainCanvas(doc);
  if (terr) {
    g.save();
    if (derived.plotRing && derived.plotRing.length >= 3) { trace(derived.plotRing, true); g.clip(); }
    g.imageSmoothingEnabled = true;
    g.drawImage(terr, 0, 0, M, M);
    g.restore();
  }

  // 3 screenshot
  if (doc.overlay && s.overlayImage) {
    const o = doc.overlay;
    g.save();
    g.globalAlpha = o.opacity;
    g.translate(o.x, o.y);
    g.rotate((o.rotation * Math.PI) / 180);
    g.scale(o.scale, o.scale);
    g.imageSmoothingEnabled = true;
    g.drawImage(s.overlayImage, 0, 0);
    g.restore();
  }

  // 4 plot
  if (derived.plotRing && derived.plotRing.length >= 3 && !terr) {
    trace(derived.plotRing, true);
    g.globalAlpha = overlayOn ? 0.25 : 1;
    g.fillStyle = pal.plot;
    g.fill();
    g.globalAlpha = 1;
  }

  const issuesBad = new Set<string>();
  for (const is of s.analysis?.issues ?? []) if (is.severity === 'bad') issuesBad.add(is.targetId);
  const areas = Object.entries(doc.areas);

  // 5 habitat fills
  g.globalAlpha = overlayOn ? 0.75 : 1;
  for (const [id, a] of areas) {
    if (!isAnimalArea(a)) continue;
    const ring = derived.areaRings[id];
    if (!ring || ring.length < 3) continue;
    const sp = s.species?.get(a.speciesId);
    const base = a.kind === 'aviary' ? pal.aviary : a.kind === 'aquarium' ? pal.aquarium : sp ? BIOMES[sp.biome].color : '#9a9a8a';
    trace(ring, true);
    g.fillStyle = shade(base, ((idHash(id) % 7) - 3) * 0.045);
    g.fill();
    if (a.kind === 'aviary') {
      // a light net pattern for aviaries
      g.save(); g.clip();
      g.strokeStyle = 'rgba(255,255,255,0.35)'; g.lineWidth = u;
      const [x0, y0, x1, y1] = ringBox(ring);
      g.beginPath();
      for (let x = x0 - (y1 - y0); x < x1; x += 6) { g.moveTo(x, y0); g.lineTo(x + (y1 - y0), y1); }
      g.stroke(); g.restore();
    }
  }
  g.globalAlpha = 1;

  // 6 plaza and staff area
  for (const [id, a] of areas) {
    if (a.kind !== 'plaza' && a.kind !== 'backOfHouse') continue;
    const ring = derived.areaRings[id];
    if (!ring || ring.length < 3) continue;
    trace(ring, true);
    g.fillStyle = a.kind === 'plaza' ? pal.plaza : pal.boh;
    g.fill();
    g.lineWidth = (isSel('area', id) ? 4 : 1.5) * u;
    g.strokeStyle = isSel('area', id) ? '#fff' : 'rgba(0,0,0,0.35)';
    g.stroke();
  }

  // 7 water
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const [id, rv] of Object.entries(doc.rivers)) {
    const p = derived.riverPolys[id];
    if (!p || p.length < 2) continue;
    trace(p, false);
    g.strokeStyle = pal.waterEdge; g.lineWidth = rv.w + 3 * u; g.stroke();
    g.strokeStyle = pal.water; g.lineWidth = rv.w; g.stroke();
    if (isSel('river', id)) { g.strokeStyle = '#fff'; g.lineWidth = 2 * u; g.setLineDash([8 * u, 6 * u]); g.stroke(); g.setLineDash([]); }
  }
  for (const [id, a] of areas) {
    if (a.kind !== 'lake' && a.kind !== 'pool') continue;
    const ring = derived.areaRings[id];
    if (!ring || ring.length < 3) continue;
    trace(ring, true);
    g.fillStyle = pal.water; g.fill();
    g.lineWidth = (isSel('area', id) ? 4 : 2) * u;
    g.strokeStyle = isSel('area', id) ? '#fff' : pal.waterEdge;
    g.stroke();
  }

  // 8 habitat outlines
  for (const [id, a] of areas) {
    if (!isAnimalArea(a)) continue;
    const ring = derived.areaRings[id];
    if (!ring || ring.length < 3) continue;
    const bad = issuesBad.has(id), on = isSel('area', id);
    trace(ring, true);
    g.lineWidth = (on ? 4.5 : bad ? 3 : 2) * u;
    g.strokeStyle = on ? '#ffffff' : bad ? '#e74c3c' : 'rgba(255,255,255,0.85)';
    if (a.locked && !on) g.setLineDash([7 * u, 4 * u]);
    g.stroke();
    g.setLineDash([]);
  }

  // 9 roads: each short segment stroked at its own width, so a road can taper
  const water = waterOf(doc, derived);
  const roads = Object.entries(doc.roads);
  const strokeSegs = (P: Pt[], W: number[], extra: number, colour: string, only?: (i: number) => boolean) => {
    g.strokeStyle = colour;
    for (let i = 0; i < P.length - 1; i++) {
      if (only && !only(i)) continue;
      g.beginPath(); g.moveTo(P[i]![0], P[i]![1]); g.lineTo(P[i + 1]![0], P[i + 1]![1]);
      g.lineWidth = ((W[i] ?? 0) + (W[i + 1] ?? 0)) / 2 + extra;
      g.stroke();
    }
  };
  const hoverRoad = s.preview?.hoverRoad ?? null;
  for (const [id] of roads) {
    const p = derived.roadPolys[id];
    if (!p || p.pts.length < 2) continue;
    strokeSegs(p.pts, p.widths, (isSel('road', id) || hoverRoad === id ? 6 : 4) * u, isSel('road', id) ? '#ffffff' : hoverRoad === id ? '#ff9800' : pal.roadEdge);
  }
  for (const [id] of roads) {
    const p = derived.roadPolys[id];
    if (!p || p.pts.length < 2) continue;
    const loose = s.analysis?.roads[id] && !s.analysis.roads[id]!.connected;
    strokeSegs(p.pts, p.widths, 0, loose ? pal.roadLoose : pal.road);
  }
  g.lineCap = 'butt';
  for (const [id] of roads) {
    const p = derived.roadPolys[id];
    if (!p || p.pts.length < 2) continue;
    const wet = wetSegments(p, water);
    if (!wet.some(Boolean)) continue;
    strokeSegs(p.pts, p.widths, 4 * u, pal.timberEdge, (i) => !!wet[i]);
    strokeSegs(p.pts, p.widths, 0, pal.timber, (i) => !!wet[i]);
  }
  g.lineCap = 'round';

  // 10 labels: largest zones first, the selected one always; a label that would overlap one already drawn,
  // or doesn't fit inside its zone at this zoom, is shortened (name only, then without the count) or left out
  // until you zoom in further
  if (s.labels !== false) {
    drawLabels(g, s, k1, zoom, u, sel);
  }

  // 11 icons
  g.textAlign = 'center';
  for (const [id, ic] of Object.entries(doc.icons)) {
    const d = ICONS[ic.type], big = ic.type === 'entrance', R = (big ? ICON_R_ENTRANCE : ICON_R) * u;
    const del = s.preview?.hoverDelete && s.preview.hoverDelete.kind === 'icon' && s.preview.hoverDelete.id === id;
    g.beginPath(); g.arc(ic.x, ic.y, R, 0, Math.PI * 2);
    g.fillStyle = d.color; g.fill();
    g.lineWidth = (isSel('icon', id) || del ? 4 : 2) * u;
    g.strokeStyle = del ? '#e74c3c' : isSel('icon', id) ? '#ffeb3b' : '#fff';
    g.stroke();
    g.font = `${(big ? 17 : 12) * u}px system-ui, sans-serif`;
    g.textBaseline = 'middle'; g.fillStyle = '#fff';
    g.fillText(d.icon, ic.x, ic.y + u);
    g.textBaseline = 'alphabetic';
    if (big) {
      g.font = `bold ${12 * u}px system-ui, sans-serif`;
      g.strokeStyle = 'rgba(0,0,0,0.75)'; g.lineWidth = 3 * u;
      g.strokeText('ENTRANCE', ic.x, ic.y + 32 * u); g.fillText('ENTRANCE', ic.x, ic.y + 32 * u);
    }
  }
  g.textAlign = 'left';

  // 12 boundary
  if (derived.plotRing && derived.plotRing.length >= 3) {
    const on = sel?.kind === 'plot';
    trace(derived.plotRing, true);
    g.lineWidth = (on ? 4 : 3) * u;
    g.strokeStyle = on ? '#ffeb3b' : pal.dark ? '#d8d8c8' : '#2b2b22';
    g.setLineDash([12 * u, 6 * u]); g.stroke(); g.setLineDash([]);
  }

  // delete highlight for areas, roads, rivers
  const hd = s.preview?.hoverDelete;
  if (hd && hd.kind !== 'icon' && hd.kind !== 'plot') {
    const shape = hd.kind === 'area' ? derived.areaRings[hd.id] : hd.kind === 'road' ? derived.roadPolys[hd.id]?.pts : derived.riverPolys[hd.id];
    if (shape && shape.length >= 2) {
      trace(shape, hd.kind === 'area');
      g.lineWidth = 4 * u; g.strokeStyle = '#e74c3c'; g.setLineDash([6 * u, 4 * u]); g.stroke(); g.setLineDash([]);
    }
  }

  // 13 grid
  if (s.showGrid) {
    const step = gridStep(M);
    g.lineWidth = u;
    g.strokeStyle = pal.dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.13)';
    g.beginPath();
    for (let v = 0; v <= M + 0.01; v += step) { g.moveTo(v, 0); g.lineTo(v, M); g.moveTo(0, v); g.lineTo(M, v); }
    g.stroke();
    g.save();
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.font = '12px system-ui, sans-serif';
    g.fillStyle = 'rgba(255,255,255,0.95)'; g.strokeStyle = 'rgba(0,0,0,0.6)'; g.lineWidth = 3;
    const top = Math.max(14, t.oy + 14), left = Math.max(5, t.ox + 5);
    for (let i = 0; i * step < M; i++) {
      const sx = t.ox + i * step * k + 5, sy = t.oy + i * step * k + 14;
      if (sx > -10 && sx < t.w - 10) { g.strokeText(colName(i), sx, top); g.fillText(colName(i), sx, top); }
      if (sy > top + 12 && sy < t.h) { g.strokeText(String(i + 1), left, sy); g.fillText(String(i + 1), left, sy); }
    }
    g.restore();
  }

  // 14 handles and width grips
  if (s.handles && sel) {
    for (const h of selectionHandles(doc, sel)) {
      g.beginPath();
      g.arc(h.x, h.y, (h.shared ? 7.5 : 6.5) * u, 0, Math.PI * 2);
      g.fillStyle = h.shared ? '#ffeb3b' : '#fff'; g.fill();
      g.lineWidth = 2 * u; g.strokeStyle = '#222'; g.stroke();
    }
    if (s.grips) {
      for (const wh of widthGrips(doc, derived, sel)) {
        g.beginPath(); g.moveTo(wh.a[0], wh.a[1]); g.lineTo(wh.b[0], wh.b[1]);
        g.lineWidth = 1.5 * u; g.strokeStyle = '#ff9800'; g.stroke();
        for (const q of [wh.a, wh.b]) {
          const r = 5.5 * u;
          g.fillStyle = '#ff9800'; g.fillRect(q[0] - r, q[1] - r, 2 * r, 2 * r);
          g.lineWidth = 1.5 * u; g.strokeStyle = '#fff'; g.strokeRect(q[0] - r, q[1] - r, 2 * r, 2 * r);
        }
        const lx = wh.a[0] + wh.nx * 15 * u, ly = wh.a[1] + wh.ny * 15 * u, txt = `${wh.w.toFixed(1).replace(/\.0$/, '')} m`;
        g.font = `bold ${11 * u}px system-ui, sans-serif`;
        g.textAlign = 'center'; g.textBaseline = 'middle';
        g.lineWidth = 3 * u; g.strokeStyle = 'rgba(0,0,0,0.75)'; g.fillStyle = '#ffcc80';
        g.strokeText(txt, lx, ly); g.fillText(txt, lx, ly);
        g.textAlign = 'left'; g.textBaseline = 'alphabetic';
      }
    }
  }

  // 15 tool previews
  const pv = s.preview;
  if (pv) {
    if (pv.ring) {
      g.beginPath(); g.arc(pv.ring[0], pv.ring[1], 11 * u, 0, Math.PI * 2);
      g.lineWidth = 3.5 * u; g.strokeStyle = '#ff9800'; g.stroke();
    }
    if (pv.cutAt) {
      g.beginPath(); g.moveTo(pv.cutAt.a[0], pv.cutAt.a[1]); g.lineTo(pv.cutAt.b[0], pv.cutAt.b[1]);
      g.lineWidth = 3 * u; g.strokeStyle = '#e74c3c'; g.setLineDash([5 * u, 3 * u]); g.stroke(); g.setLineDash([]);
    }
    const pts = (pv.points ?? []).concat(pv.cursor ? [pv.cursor] : []);
    if (pts.length >= 2) {
      const shape = pv.curved ? spline(pts, false) : pts;
      trace(shape, !!pv.closed && pts.length >= 3);
      if (pv.closed && pts.length >= 3) { g.fillStyle = 'rgba(255,235,59,0.18)'; g.fill(); }
      g.lineWidth = 3 * u; g.strokeStyle = '#ffeb3b'; g.setLineDash([8 * u, 6 * u]); g.stroke(); g.setLineDash([]);
    }
    for (const p of pv.points ?? []) {
      g.beginPath(); g.arc(p[0], p[1], 5 * u, 0, Math.PI * 2);
      g.fillStyle = '#ffeb3b'; g.fill(); g.lineWidth = 1.5 * u; g.strokeStyle = '#333'; g.stroke();
    }
    if (pv.snap) {
      g.beginPath(); g.arc(pv.snap[0], pv.snap[1], 9 * u, 0, Math.PI * 2);
      g.lineWidth = 3 * u; g.strokeStyle = '#ffeb3b'; g.stroke();
      g.beginPath(); g.arc(pv.snap[0], pv.snap[1], 3 * u, 0, Math.PI * 2);
      g.fillStyle = '#ffeb3b'; g.fill();
    }
  }

  // 16 scale bar
  if (s.scaleBar !== false) {
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const nice = [5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
    const metres = nice.find((m) => m * k >= 80) ?? 1000, bar = metres * k;
    const x = 14, y = t.h - 22 - (s.scaleBarLift ?? 0);
    g.fillStyle = '#fff'; g.lineWidth = 3; g.strokeStyle = 'rgba(0,0,0,0.7)';
    g.font = 'bold 12px system-ui, sans-serif';
    g.strokeRect(x, y, bar, 6); g.fillRect(x, y, bar, 6);
    g.strokeText(`${metres} m`, x, y - 6); g.fillText(`${metres} m`, x, y - 6);
  }
  g.setTransform(1, 0, 0, 1, 0, 0);
}

/** Draws the zone labels; see the comment where it's called. */
function drawLabels(g: CanvasRenderingContext2D, s: Scene, k1: number, zoom: number, u: number, sel: Selection): void {
  const { doc, derived } = s;
  interface Cand { id: string; ring: Pt[]; area: number; selected: boolean; animal: boolean; full: string; short: string; bare: string; sub: string | null }
  const cands: Cand[] = [];
  for (const [id, a] of Object.entries(doc.areas)) {
    if (a.kind === 'pool') continue;
    const ring = derived.areaRings[id];
    if (!ring || ring.length < 3) continue;
    const area = polyArea(ring), selected = !!sel && sel.kind === 'area' && sel.id === id;
    if (isAnimalArea(a)) {
      const sp = s.species?.get(a.speciesId), icon = KIND_ICON[a.kind] ? `${KIND_ICON[a.kind]} ` : '', lock = a.locked ? '🔒 ' : '';
      const nm = sp ? sp.name : 'Unknown animal';
      const st = s.analysis?.habitats[id];
      const sub = st && st.need > 0 ? `${fmt(area)} of ${fmt(st.need)} m²` : `${fmt(area)} m²`;
      cands.push({ id, ring, area, selected, animal: true, full: `${lock}${icon}${nm} ×${a.count}`, short: `${lock}${icon}${nm} ×${a.count}`, bare: `${icon}${nm}`, sub });
    } else {
      const nm = KIND_LABEL[a.kind];
      cands.push({ id, ring, area, selected, animal: false, full: nm, short: nm, bare: nm, sub: null });
    }
  }
  cands.sort((p, q) => (p.selected === q.selected ? q.area - p.area : p.selected ? -1 : 1));
  const placed: [number, number, number, number][] = [];
  const pad = 2 * u;
  const free = (b: [number, number, number, number]) => !placed.some((o) => b[0] < o[2] + pad && o[0] < b[2] + pad && b[1] < o[3] + pad && o[1] < b[3] + pad);
  g.textAlign = 'center';
  g.textBaseline = 'alphabetic';
  for (const c of cands) {
    const [x0, y0, x1, y1] = ringBox(c.ring), zw = (x1 - x0) * 0.92, zh = (y1 - y0) * 0.85, at = labelPoint(c.ring);
    // the prototype's size at zoom 1; zoomed in, labels grow more slowly than the map, so more of them fit
    const fs = (Math.min(20, Math.max(9, Math.sqrt(c.area / Math.PI) * k1 * 0.24)) / k1) / Math.sqrt(zoom);
    const small = Math.max(fs * 0.8, 9 * u);
    const widthOf = (text: string, size: number, bold: boolean) => { g.font = `${bold ? 'bold ' : ''}${size}px system-ui, sans-serif`; return g.measureText(text).width; };
    // ways to show it, most detailed first: name + area line, name, smaller name, name without the count
    const tries: { name: string; size: number; sub: boolean }[] = [
      ...(c.sub ? [{ name: c.full, size: fs, sub: true }] : []),
      { name: c.full, size: fs, sub: false },
      { name: c.short, size: small, sub: false },
      { name: c.bare, size: small, sub: false },
    ];
    let pick: { name: string; size: number; sub: boolean; box: [number, number, number, number] } | null = null;
    for (const t of tries) {
      const w = Math.max(widthOf(t.name, t.size, true), t.sub && c.sub ? widthOf(c.sub, t.size * 0.78, false) : 0);
      const h = t.size * (t.sub ? 2.1 : 1.15);
      const box: [number, number, number, number] = [at[0] - w / 2, at[1] - t.size, at[0] + w / 2, at[1] - t.size + h];
      const fits = w <= zw && h <= zh;
      if ((fits && free(box)) || (c.selected && t === tries[0])) { pick = { ...t, box }; break; }
    }
    if (!pick) continue;
    placed.push(pick.box);
    g.font = `bold ${pick.size}px system-ui, sans-serif`;
    g.fillStyle = '#fff'; g.strokeStyle = 'rgba(0,0,0,0.7)'; g.lineWidth = 3.5 * u;
    g.strokeText(pick.name, at[0], at[1]); g.fillText(pick.name, at[0], at[1]);
    if (pick.sub && c.sub) {
      g.font = `${pick.size * 0.78}px system-ui, sans-serif`;
      g.strokeText(c.sub, at[0], at[1] + pick.size * 1.05); g.fillText(c.sub, at[0], at[1] + pick.size * 1.05);
    }
  }
}

function ringBox(ring: readonly Pt[]): [number, number, number, number] {
  let a = Infinity, b = Infinity, c = -Infinity, d = -Infinity;
  for (const p of ring) { a = Math.min(a, p[0]); b = Math.min(b, p[1]); c = Math.max(c, p[0]); d = Math.max(d, p[1]); }
  return [a, b, c, d];
}
